import { createElement, useCallback } from 'react';
import { defineMessages } from 'react-intl';

import type { AiSuggestionsRightRailEntryPoint } from '@atlaskit/editor-common/analytics';

import { ContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import { getConfluenceContentAri } from '@confluence/content-types-utils/entry-points/getConfluenceContentAri.ts';
import {
	useObjectSidebarActions,
	usePanel,
	useIsObjectSidebarShown,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { PanelName } from '@confluence/object-sidebar-api/entry-points/PanelName.ts';
import { useContentType } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	resetPublishedSuggestions,
	startPublishedSuggestions,
} from './published/publishedSuggestionsStore.ts';
import { cancelLiveDocEditModeSelection } from './live/liveDocEditModeSelection.ts';
import { isViewModeSuggestionsEnabled } from './isViewModeSuggestionsEnabled.ts';
import { SuggestionsPanel } from './components/SuggestionsPanel.tsx';
import { SuggestionsCount } from './components/SuggestionsHeader.tsx';
import { SuggestionsHeaderActionGroup } from './components/SuggestionsHeaderActionGroup.tsx';

const i18n = defineMessages({
	suggestionsPanelHeaderLabel: {
		id: 'suggestions-panel.panel.header.review-suggestions.label',
		defaultMessage: 'Review suggestions',
		description:
			'The text is shown as the header label at the top of the AI suggestions sidebar panel, identifying the panel to the user.',
	},
});

type ShowSuggestionsPanelOptions = {
	entryPoint?: AiSuggestionsRightRailEntryPoint;
	/**
	 * Set when opening the panel on a published page. There is no editor to read suggestions from,
	 * so the panel lists the suggestions fetched for this content.
	 */
	publishedContentId?: string;
};

const onSuggestionsPanelClose = (): void => {
	resetPublishedSuggestions();
	cancelLiveDocEditModeSelection();
};

export const useShowSuggestionsPanel = (): {
	isSuggestionsPanelShown: boolean;
	showSuggestionsPanel: (options?: ShowSuggestionsPanelOptions) => void;
	hideSuggestionsPanel: (afterCloseCallback?: () => void) => Promise<void>;
} => {
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const panel = usePanel();
	const { showObjectSidebar, hideObjectSidebar, changePanel } = useObjectSidebarActions();
	const { activationId, cloudId, isAdminHubAIEnabled, isAIEarlyAccessEnabled } = useSessionData();
	const [contentType = ContentType.PAGE] = useContentType();

	const startPublishedSuggestionsIfEnabled = useCallback(
		(publishedContentId: string | undefined): void => {
			if (publishedContentId && isViewModeSuggestionsEnabled()) {
				startPublishedSuggestions(
					publishedContentId,
					getConfluenceContentAri({
						siteId: cloudId,
						resourceType: contentType,
						activationId,
						resourceId: publishedContentId,
					}),
				);
			}
		},
		[activationId, cloudId, contentType],
	);

	const showSuggestionsPanel = useCallback(
		({ entryPoint, publishedContentId }: ShowSuggestionsPanelOptions = {}): void => {
			if (!isAdminHubAIEnabled || !isAIEarlyAccessEnabled) {
				return;
			}

			startPublishedSuggestionsIfEnabled(publishedContentId);

			const suggestionsPanel = {
				id: PanelName.SuggestionsPanel,
				headerComponentElements: {
					headerLabel: i18n.suggestionsPanelHeaderLabel,
					HeaderAfterIconElement: SuggestionsCount,
					// Renders the more menu followed by the filter menu; the panel header appends its
					// close button after this element, so the filter button sits between the two.
					HeaderRightAlignedElement: SuggestionsHeaderActionGroup,
				},
				BodyComponent: () => createElement(SuggestionsPanel, { entryPoint }),
				closeOptions: { onPanelClose: onSuggestionsPanelClose },
			};

			if (isObjectSidebarShown) {
				void changePanel(suggestionsPanel, 'push');
				return;
			}

			showObjectSidebar(suggestionsPanel, 'push');
		},
		[
			changePanel,
			isAdminHubAIEnabled,
			isAIEarlyAccessEnabled,
			isObjectSidebarShown,
			showObjectSidebar,
			startPublishedSuggestionsIfEnabled,
		],
	);

	return {
		isSuggestionsPanelShown: isObjectSidebarShown && panel?.id === PanelName.SuggestionsPanel,
		showSuggestionsPanel,
		hideSuggestionsPanel: hideObjectSidebar,
	};
};
